// Tree operations for a list's items. The list is edited as a flat,
// depth-annotated sequence (what's on screen), and every structural change —
// drag, indent, outdent — is: change the sequence, normalise it, then diff it
// back into { parent_id, depth, sort_order } moves for the server.
//
// A card normally shows a whole list. A project's Lists card shows one branch
// of the project instead: its rows start under `rootParent`, sit `depthOffset`
// levels deeper in the project than on screen, and only `maxDepth` levels are
// shown (deeper lines stay put and move with their parent). TreeShape carries
// that; the default is a plain list.
import { TaskItem } from '../types/tasks';

export const MAX_DEPTH = 2;
/** How deep a project's mind map goes (api/utils/tasks/core.ts maxDepthFor). */
export const PROJECT_MAX_DEPTH = 8;

export interface TreeShape {
  /** Deepest on-screen depth (0-based). */
  maxDepth: number;
  /** Parent of the on-screen top-level rows (null = the list's own top level). */
  rootParent: number | null;
  /** Real depth of an on-screen top-level row. */
  depthOffset: number;
}

export const LIST_SHAPE: TreeShape = { maxDepth: MAX_DEPTH, rootParent: null, depthOffset: 0 };

export interface FlatRow {
  item: TaskItem;
  depth: number;
}

export function byOrder(a: TaskItem, b: TaskItem) {
  return a.sort_order - b.sort_order || a.id - b.id;
}

export function childrenOf(items: TaskItem[]): Map<number | null, TaskItem[]> {
  const map = new Map<number | null, TaskItem[]>();
  for (const item of items) {
    const key = item.parent_id ?? null;
    const list = map.get(key) || [];
    list.push(item);
    map.set(key, list);
  }
  map.forEach((list) => list.sort(byOrder));
  return map;
}

/**
 * Depth-first rows for the given roots and their descendants, down to
 * `maxDepth`, leaving out what's under a folded (`collapsed`) item.
 */
export function flattenRoots(
  roots: TaskItem[],
  kids: Map<number | null, TaskItem[]>,
  maxDepth = Infinity,
  collapsed?: Set<number>
): FlatRow[] {
  const out: FlatRow[] = [];
  const walk = (item: TaskItem, depth: number) => {
    out.push({ item, depth });
    if (depth >= maxDepth || collapsed?.has(item.id)) return;
    for (const child of kids.get(item.id) || []) walk(child, depth + 1);
  };
  roots.forEach((root) => walk(root, 0));
  return out;
}

/** Index one past the last row of the block starting at `index` (the row and its sub-tree). */
export function blockEnd(rows: FlatRow[], index: number): number {
  const depth = rows[index].depth;
  let end = index + 1;
  while (end < rows.length && rows[end].depth > depth) end++;
  return end;
}

/** Each row may sit at most one level below the row above it, and the first row at 0. */
export function normalize(rows: FlatRow[], maxDepth = MAX_DEPTH): FlatRow[] {
  const out: FlatRow[] = [];
  rows.forEach((row, i) => {
    const max = i === 0 ? 0 : Math.min(maxDepth, out[i - 1].depth + 1);
    out.push({ ...row, depth: Math.max(0, Math.min(row.depth, max)) });
  });
  return out;
}

export interface Move {
  id: number;
  parent_id: number | null;
  depth: number;
  sort_order: number;
}

/**
 * Turns a normalised sequence back into parent / depth / sort_order and
 * returns only the rows whose position actually changed. Sort orders are
 * renumbered from `baseOrder` per sibling group so they never collide.
 */
export function toMoves(rows: FlatRow[], shape: TreeShape = LIST_SHAPE, baseOrder = 1): Move[] {
  const lastAtDepth: (number | null)[] = [];
  const counters = new Map<number | null, number>();
  const moves: Move[] = [];
  for (const row of rows) {
    const parent = row.depth === 0 ? shape.rootParent : lastAtDepth[row.depth - 1] ?? null;
    const next = (counters.get(parent) ?? baseOrder - 1) + 1;
    counters.set(parent, next);
    lastAtDepth[row.depth] = row.item.id;
    lastAtDepth.length = row.depth + 1;

    const { item } = row;
    const depth = row.depth + shape.depthOffset;
    if (item.parent_id !== parent || item.depth !== depth || item.sort_order !== next) {
      moves.push({ id: item.id, parent_id: parent, depth, sort_order: next });
    }
  }
  return moves;
}

/** Moves the block at `from` so it starts at `to` (an index into the rows without the block). */
export function moveBlock(rows: FlatRow[], from: number, to: number, maxDepth = MAX_DEPTH): FlatRow[] {
  const end = blockEnd(rows, from);
  const block = rows.slice(from, end);
  const rest = [...rows.slice(0, from), ...rows.slice(end)];
  const at = Math.max(0, Math.min(to, rest.length));
  // The block keeps its shape. Its root stays at its own depth where the spot
  // allows it (at most one below the row it lands after); dropped between a
  // parent and its first child, it becomes a sibling of that child.
  const prev = rest[at - 1];
  const next = rest[at];
  const rootDepth = block[0].depth;
  const subtreeExtra = Math.max(...block.map((r) => r.depth - rootDepth));
  let newRoot = prev ? Math.min(rootDepth, prev.depth + 1) : 0;
  if (prev && next && next.depth > newRoot) newRoot = Math.min(next.depth, prev.depth + 1);
  newRoot = Math.max(0, Math.min(newRoot, maxDepth - subtreeExtra));
  const shift = newRoot - rootDepth;
  const moved = block.map((r) => ({ ...r, depth: r.depth + shift }));
  return normalize([...rest.slice(0, at), ...moved, ...rest.slice(at)], maxDepth);
}

export function canIndent(rows: FlatRow[], index: number, maxDepth = MAX_DEPTH): boolean {
  if (index <= 0) return false;
  const end = blockEnd(rows, index);
  const maxInBlock = Math.max(...rows.slice(index, end).map((r) => r.depth));
  return rows[index].depth <= rows[index - 1].depth && maxInBlock < maxDepth;
}

export function indent(rows: FlatRow[], index: number, maxDepth = MAX_DEPTH): FlatRow[] {
  if (!canIndent(rows, index, maxDepth)) return rows;
  const end = blockEnd(rows, index);
  return normalize(rows.map((r, i) => (i >= index && i < end ? { ...r, depth: r.depth + 1 } : r)), maxDepth);
}

/**
 * Outdent moves the block out from under its parent and places it right
 * after the parent's sub-tree, so the parent's other children stay put.
 */
export function outdent(rows: FlatRow[], index: number, maxDepth = MAX_DEPTH): FlatRow[] {
  const depth = rows[index].depth;
  if (depth === 0) return rows;
  let parentIndex = index - 1;
  while (parentIndex >= 0 && rows[parentIndex].depth >= depth) parentIndex--;
  if (parentIndex < 0) return rows;

  const end = blockEnd(rows, index);
  const block = rows.slice(index, end).map((r) => ({ ...r, depth: r.depth - 1 }));
  const rest = [...rows.slice(0, index), ...rows.slice(end)];
  const parentEnd = blockEnd(rest, parentIndex);
  return normalize([...rest.slice(0, parentEnd), ...block, ...rest.slice(parentEnd)], maxDepth);
}

/** Mean of the children, like the server does it, for instant feedback. */
export function recomputeProgress(items: Map<number, TaskItem>, startParent: number | null) {
  let current = startParent;
  let guard = 0;
  while (current && guard++ < 12) {
    const parentId: number = current;
    const parent = items.get(parentId);
    if (!parent) return;
    const kids = Array.from(items.values()).filter((i) => i.parent_id === parentId && !i.deleted_at);
    if (!kids.length) return;
    const avg = Math.round(kids.reduce((sum, k) => sum + k.progress, 0) / kids.length);
    items.set(parentId, {
      ...parent,
      progress: avg,
      completed_at: avg === 100 ? parent.completed_at || new Date().toISOString() : null,
    });
    current = parent.parent_id;
  }
}

export function descendantIds(items: TaskItem[], id: number): number[] {
  const kids = childrenOf(items.filter((i) => !i.deleted_at));
  const out: number[] = [];
  const walk = (parent: number) => {
    for (const child of kids.get(parent) || []) {
      out.push(child.id);
      walk(child.id);
    }
  };
  walk(id);
  return out;
}

/** Everything under a node (the whole list for null). */
export function subtreeOf(items: TaskItem[], rootId: number | null): TaskItem[] {
  if (rootId === null) return items;
  const kids = childrenOf(items.filter((i) => !i.deleted_at));
  const out: TaskItem[] = [];
  const walk = (parent: number) => {
    for (const child of kids.get(parent) || []) {
      out.push(child);
      walk(child.id);
    }
  };
  walk(rootId);
  return out;
}
